import type {
    MessageChannel,
    MessageChannels,
    MessageValue,
    ValidationConfig,
    ValidationContext,
    ValidationEngineRule
} from './types';
import type { MessageResolveFn, MessageTemplateContext } from './messageResolver';
import { interpolateLabelTemplate } from './messageResolver';
import { isCollectionNode, isFieldNode, isGroupNode } from './guards';
import { hasLabelTemplateToken } from './utils/messageTemplates';

export type TransformValidationConfigOptions = {
    /**
     * Static label map used to pre-interpolate `${label...}` tokens.
     *
     * Keys can be:
     * - full paths (e.g. `buyer.address.streetAddress`)
     * - normalized paths without indices (e.g. `items.field`)
     * - leaf keys (e.g. `streetAddress`)
     */
    fieldLabels?: Record<string, string>;
    /**
     * Optional resolver for `{ key }` message values.
     * If provided, keyed messages can also be pre-interpolated.
     */
    resolveFn?: MessageResolveFn;
    /** Optional prefix to apply to generated ctx.path values while traversing. */
    pathPrefix?: string;
};

function buildTemplateCtx(path: string, options: TransformValidationConfigOptions): MessageTemplateContext {
    return {
        path,
        fieldLabels: options.fieldLabels
    };
}

function resolveMessageValue(
    value: MessageValue | undefined,
    channel: MessageChannel,
    ctx: MessageTemplateContext,
    resolveFn?: MessageResolveFn
): string | MessageValue | undefined {
    if (!value) return undefined;

    if (typeof value === 'string') {
        if (!hasLabelTemplateToken(value)) return value;
        return interpolateLabelTemplate(value, channel, ctx);
    }

    // Keyed message: only resolvable when a resolver is provided.
    const key = (value as { key?: string }).key;
    if (!key || !resolveFn) return value;

    const resolved = resolveFn(channel, key, ctx as ValidationContext);
    if (!hasLabelTemplateToken(resolved)) return resolved;
    return interpolateLabelTemplate(resolved, channel, ctx);
}

function transformRuleMessages(
    messages: ValidationEngineRule['messages'],
    ctx: MessageTemplateContext,
    resolveFn?: MessageResolveFn
): ValidationEngineRule['messages'] {
    // Single value: potentially produce per-channel outputs if they differ.
    if (typeof messages === 'string' || ('key' in (messages as any) && typeof (messages as any).key === 'string')) {
        const inline = resolveMessageValue(messages as MessageValue, 'inline', ctx, resolveFn);
        const gcalert = resolveMessageValue(messages as MessageValue, 'gcalert', ctx, resolveFn);

        // If we couldn't resolve keyed values, keep original.
        if (typeof inline !== 'string' || typeof gcalert !== 'string') return messages;
        if (inline === gcalert) return inline;
        return { inline, gcalert };
    }

    const chans = messages as MessageChannels;
    const out: MessageChannels = { ...chans };

    const inline = resolveMessageValue(chans.inline, 'inline', ctx, resolveFn);
    const gcalert = resolveMessageValue(chans.gcalert, 'gcalert', ctx, resolveFn);

    if (inline !== undefined) out.inline = inline as any;
    if (gcalert !== undefined) out.gcalert = gcalert as any;

    return out;
}

function transformRule(
    rule: ValidationEngineRule,
    ctx: MessageTemplateContext,
    resolveFn?: MessageResolveFn
): ValidationEngineRule {
    if (!rule.messages) return rule;
    return {
        ...rule,
        messages: transformRuleMessages(rule.messages, ctx, resolveFn)
    };
}

function joinPath(parts: string[], prefix?: string): string {
    const p = parts.filter(Boolean).join('.');
    if (!prefix) return p;
    return `${prefix.replace(/\.$/, '')}.${p}`.replace(/^\./, '');
}

function transformNode(node: any, pathParts: string[], options: TransformValidationConfigOptions): any {
    if (!node || typeof node !== 'object') return node;

    if (isFieldNode(node as any)) {
        const path = joinPath(pathParts, options.pathPrefix);
        const ctx = buildTemplateCtx(path, options);
        return {
            ...node,
            rules: node.rules.map((r: ValidationEngineRule) => transformRule(r, ctx, options.resolveFn))
        };
    }

    if (isGroupNode(node as any)) {
        return {
            ...node,
            $group: transformValidationConfig(node.$group, options, pathParts)
        };
    }

    if (isCollectionNode(node as any)) {
        return {
            ...node,
            $collection: transformValidationConfig(node.$collection, options, pathParts)
        };
    }

    return transformValidationConfig(node as ValidationConfig, options, pathParts);
}

export function transformValidationConfig(
    config: ValidationConfig,
    options: TransformValidationConfigOptions,
    _pathParts: string[] = []
): ValidationConfig {
    const out: ValidationConfig = {};

    for (const [key, node] of Object.entries(config)) {
        out[key] = transformNode(node, [..._pathParts, key], options);
    }

    return out;
}

// Alias matching common usage in configs.
export const transformConfig = transformValidationConfig;
